import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Footer from '../../components/feature/Footer';
import BrandCard from './components/BrandCard';
import EmptyState from './components/EmptyState';
import ErrorState from '../../components/base/ErrorState';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Brand {
  id: number;
  name: string;
  logo_url?: string;
  country?: string;
  description?: string;
  product_count?: number;
}

type SortOption = 'name_asc' | 'name_desc' | 'products_desc' | 'products_asc';

const BrandsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const searchKeyword = searchParams.get('search') || '';
  const sortBy = (searchParams.get('sort') || 'name_asc') as SortOption;
  const pageSize = parseInt(searchParams.get('pageSize') || '24');
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    loadBrands();
  }, [searchKeyword, sortBy, pageSize, currentPage]);

  const loadBrands = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query - only read from brands table
      let query = supabase
        .from('brands')
        .select('id, name, logo_url, country, description', { count: 'exact' });

      // Search filter: name ILIKE '%keyword%'
      if (searchKeyword.trim()) {
        query = query.ilike('name', `%${searchKeyword}%`);
      }

      // Sorting (name-based sorts can be done in query)
      if (sortBy === 'name_asc') {
        query = query.order('name', { ascending: true });
      } else if (sortBy === 'name_desc') {
        query = query.order('name', { ascending: false });
      } else {
        // For product count sorting, we'll sort client-side after loading product counts
        query = query.order('name', { ascending: true });
      }

      // Pagination
      const offset = (currentPage - 1) * pageSize;
      query = query.range(offset, offset + pageSize - 1);

      const { data: brandsData, count, error: brandsError } = await query;

      if (brandsError) {
        console.error('Error loading brands:', brandsError);
        setLoading(false);
        setError(brandsError.message || 'Failed to load brands');
        return;
      }

      if (!brandsData) {
        setBrands([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      setTotalCount(count || 0);

      // Load product count for each brand (read-only, no schema changes)
      const brandIds = brandsData.map(b => b.id);
      const { data: productCounts } = await supabase
        .from('products')
        .select('brand_id')
        .eq('status', 'active')
        .in('brand_id', brandIds);

      // Count products per brand (client-side aggregation)
      const countsMap: Record<number, number> = {};
      if (productCounts) {
        productCounts.forEach(p => {
          if (p.brand_id) {
            countsMap[p.brand_id] = (countsMap[p.brand_id] || 0) + 1;
          }
        });
      }

      // Enrich brands with product counts
      let enrichedBrands: Brand[] = brandsData.map(b => ({
        ...b,
        product_count: countsMap[b.id] || 0,
      }));

      // Client-side sorting for product count (no DB schema changes)
      if (sortBy === 'products_desc') {
        enrichedBrands.sort((a, b) => (b.product_count || 0) - (a.product_count || 0));
      } else if (sortBy === 'products_asc') {
        enrichedBrands.sort((a, b) => (a.product_count || 0) - (b.product_count || 0));
      }

      setBrands(enrichedBrands);
      setLoading(false);
    } catch (error) {
      console.error('Error loading brands:', error);
      setLoading(false);
      setError((error as any).message || 'Failed to load brands');
    }
  };

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    // Reset to page 1 when filters change
    if (key !== 'page') {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  const clearSearch = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('search');
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-gray-400 animate-spin"></i>
            <p className="mt-4 text-gray-600">Loading brands...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <ErrorState onRetry={loadBrands} />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Brands</h1>
          <p className="text-lg text-gray-600">
            Explore all brands available on GlobalShoesWholesale.com
          </p>
        </div>

        {/* Search & Sort Toolbar */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="Search brands"
                  value={searchKeyword}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchKeyword && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-5 h-5 flex items-center justify-center"
                    title="Clear search"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Sort by:
              </label>
              <select
                value={sortBy}
                onChange={(e) => updateFilter('sort', e.target.value)}
                className="px-4 py-2.5 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="name_asc">A–Z</option>
                <option value="name_desc">Z–A</option>
                <option value="products_desc">Most Products</option>
                <option value="products_asc">Least Products</option>
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-center text-sm text-gray-600 whitespace-nowrap">
              {loading ? (
                <span>Loading...</span>
              ) : (
                <span className="font-medium">
                  {totalCount} brand{totalCount !== 1 ? 's' : ''} found
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
              <p className="text-gray-600">Loading brands...</p>
            </div>
          </div>
        ) : brands.length === 0 ? (
          <EmptyState hasSearch={!!searchKeyword} onClearSearch={clearSearch} />
        ) : (
          <>
            {/* Brand Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 mb-8">
              {brands.map((brand) => (
                <BrandCard key={brand.id} brand={brand} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => updateFilter('page', String(currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Previous
                </button>

                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() => updateFilter('page', String(currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  Next
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BrandsPage;
